import { default as React, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import PublicIcon from '@mui/icons-material/Public';
import StorageIcon from '@mui/icons-material/Storage';
import LaunchIcon from '@mui/icons-material/Launch';
import { Entity } from '@backstage/catalog-model';
import {
  entityRouteParams,
  entityRouteRef,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { useRouteRef } from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router-dom';
import {
  isUserAuthorizedForEntity,
  useCurrentUserEmail,
  useSkyStageAdminEmails,
} from '../microapps/hooks';
import { PublicAppDeleteButton } from './PublicAppDeleteButton';
import { PublicAppExpiryBadge } from './PublicAppExpiry';
import {
  usePublicAppExpiry,
  type PublicAppExpiryInfo,
} from './publicAppExpiryApi';
import {
  getPublicAppDisplayName,
  getPublicAppOwners,
} from './publicAppFilters';

const CARDS_PER_PAGE = 20;
const PUBLIC_APP_URL_ANNOTATION = 'skystage.io/hosted-app-url';

// Same visual language as the internal Apps card grid (MicroappCardGrid).
const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'box-shadow 0.2s ease-in-out',
    border: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
    },
  },
  cardContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2.5),
    '&:last-child': {
      paddingBottom: theme.spacing(2.5),
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
    flex: 1,
  },
  name: {
    fontWeight: 700,
    fontSize: '1.15rem',
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  nameLink: {
    fontWeight: 700,
    fontSize: '1.15rem',
    color: '#1976d2',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    padding: '3px 6px',
    borderRadius: 4,
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
    lineHeight: 1,
  },
  publicBadge: {
    color: '#1565c0',
    backgroundColor: 'rgba(21, 101, 192, 0.1)',
  },
  storageBadge: {
    color: '#228189',
    backgroundColor: 'rgba(34, 129, 137, 0.1)',
  },
  badgeIcon: {
    fontSize: 12,
  },
  description: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    lineHeight: 1.5,
  },
  meta: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
    overflow: 'hidden',
  },
  metaLabel: {
    fontWeight: 600,
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  metaValue: {
    fontSize: '0.825rem',
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  footer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: 'auto',
  },
  footerButton: {
    textTransform: 'none',
    fontSize: '0.75rem',
    padding: '4px 10px',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(8, 2),
    color: theme.palette.text.secondary,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(8, 2),
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
  },
}));

/**
 * Empty state for both layouts: "no match" when the filter hides everything,
 * otherwise "no public apps yet".
 */
export const PublicAppEmptyState = ({
  searchActive,
  onClearSearch,
}: {
  searchActive: boolean;
  onClearSearch: () => void;
}) => {
  const classes = useStyles();
  return (
    <Box className={classes.emptyState}>
      <Typography variant="h6">
        {searchActive ? 'No apps found' : 'No public apps yet'}
      </Typography>
      <Typography variant="body2" style={{ marginTop: 4 }}>
        {searchActive
          ? 'No app matches your filter. Try a different term or clear it.'
          : 'Deploy a public app to see it here.'}
      </Typography>
      {searchActive && (
        <Button
          color="primary"
          variant="outlined"
          size="small"
          style={{ marginTop: 16, textTransform: 'none' }}
          onClick={onClearSearch}
        >
          Clear filter
        </Button>
      )}
    </Box>
  );
};

const PublicAppCard = ({
  entity,
  expiry,
}: {
  entity: Entity;
  expiry: PublicAppExpiryInfo | undefined;
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const entityRoute = useRouteRef(entityRouteRef);
  const currentUserEmail = useCurrentUserEmail();
  const adminEmails = useSkyStageAdminEmails();

  const name = getPublicAppDisplayName(entity);
  const description = entity.metadata.description ?? '';
  const appUrl = entity.metadata.annotations?.[PUBLIC_APP_URL_ANNOTATION];
  const owners = getPublicAppOwners(entity);
  const hasStorage =
    entity.metadata.annotations?.['microapp/persistentStorage'] === 'true';
  const isOwner = isUserAuthorizedForEntity(
    entity,
    currentUserEmail,
    adminEmails,
  );
  const openDetails = () => navigate(entityRoute(entityRouteParams(entity)));

  return (
    <Card className={classes.card} variant="outlined" onClick={openDetails}>
      <CardContent className={classes.cardContent}>
        <div className={classes.cardHeader}>
          <div className={classes.nameRow}>
            {appUrl ? (
              <a
                href={appUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.nameLink}
                onClick={e => e.stopPropagation()}
              >
                {name}
              </a>
            ) : (
              <Typography className={classes.name}>{name}</Typography>
            )}
            <Tooltip title="Internet-facing app secured with WellSky Identity">
              <span className={`${classes.badge} ${classes.publicBadge}`}>
                <PublicIcon className={classes.badgeIcon} />
                Public
              </span>
            </Tooltip>
            {hasStorage && (
              <Tooltip title="Has persistent storage at /data (backed up automatically)">
                <span className={`${classes.badge} ${classes.storageBadge}`}>
                  <StorageIcon className={classes.badgeIcon} />
                  Storage
                </span>
              </Tooltip>
            )}
          </div>
          {isOwner && <PublicAppDeleteButton entity={entity} />}
        </div>

        {description && (
          <Typography className={classes.description}>{description}</Typography>
        )}

        <div className={classes.meta}>
          <span className={classes.metaLabel}>Owner:</span>
          <Tooltip title={owners.join(', ')}>
            <span className={classes.metaValue}>
              {owners.length > 0 ? owners.join(', ') : '-'}
            </span>
          </Tooltip>
        </div>

        {expiry && (
          <div className={classes.meta}>
            <PublicAppExpiryBadge expiry={expiry} />
          </div>
        )}

        <div className={classes.footer}>
          {appUrl && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              href={appUrl}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<LaunchIcon style={{ fontSize: 14 }} />}
              className={classes.footerButton}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              Launch
            </Button>
          )}
          <Button
            variant="outlined"
            color="primary"
            size="small"
            className={classes.footerButton}
            onClick={e => {
              e.stopPropagation();
              openDetails();
            }}
          >
            {isOwner ? 'Manage App' : 'View Details'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * IDP-1291: card layout of the Public Hosted Apps listing. Reads the already
 * filtered entity list, so the search applies here exactly as in the table
 * layout.
 */
export const PublicAppCardGrid = ({
  searchActive,
  onClearSearch,
}: {
  searchActive: boolean;
  onClearSearch: () => void;
}) => {
  const classes = useStyles();
  const { entities, loading } = useEntityList();
  const [page, setPage] = useState(1);
  // IDP-1300: one request for the expiry dates of every card.
  const { items: expiries } = usePublicAppExpiry();

  // Back to the first page whenever the filtered result set changes.
  useEffect(() => {
    setPage(1);
  }, [entities]);

  const totalPages = Math.ceil(entities.length / CARDS_PER_PAGE);
  const pageEntities = useMemo(
    () => entities.slice((page - 1) * CARDS_PER_PAGE, page * CARDS_PER_PAGE),
    [entities, page],
  );

  if (loading && entities.length === 0) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  if (entities.length === 0) {
    return (
      <PublicAppEmptyState
        searchActive={searchActive}
        onClearSearch={onClearSearch}
      />
    );
  }

  return (
    <>
      <Grid container spacing={2}>
        {pageEntities.map(entity => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            key={`${entity.metadata.namespace ?? 'default'}/${entity.kind}/${
              entity.metadata.name
            }`}
          >
            <PublicAppCard
              entity={entity}
              expiry={expiries.get(entity.metadata.name)}
            />
          </Grid>
        ))}
      </Grid>

      {totalPages > 1 && (
        <div className={classes.pagination}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => {
              setPage(value);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            color="primary"
            shape="rounded"
          />
        </div>
      )}
    </>
  );
};
